import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { GenericInput } from "../../../Shared/components/GenericInput";
import { GenericLink } from "../../../Shared/components/GenericLink";
import { GenericButton } from "../../../Shared/components/GenericButton";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { AuthService } from "../services/AuthService";
import { RegisterInput } from "../models/RegisterInput";
import { GenericSelectInput } from "../../../Shared/components/GenericSelectInput";
import { GenericCheckbox } from "../../../Shared/components/GenericCheckbox";
import { LoginInput } from "../models/LoginInput";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { useTranslation } from "react-i18next";
export const RegisterForm = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const message = t('loading_message');
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const [country, setCountry] = useState("");
  const [countryError, setCountryError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [firstNameError, setLastNameError] = useState("");
  const [lastName, setLastName] = useState("");
  const [lastNameError, setFirstNameError] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPPassword] = useState("");
  const [confirmPasswordError, setConfirmPPasswordError] = useState("");
  const [accepted, setAccepted] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const onCountry = (val: string) => {
    setCountry(val);
    setCountryError(val ? "" : "");
  };

  const onFirstName = (val: string) => {
    setFirstName(val);
    setFirstNameError(val.trim() === "" ? "" : "");
  };
  const onMiddleName = (val: string) => {
    setMiddleName(val);
  };
  const onLastName = (val: string) => {
    setLastName(val);
    setLastNameError(val.trim() === "" ? "" : "");
  };
  const onPhoneNumber = (val: string) => {
    setPhoneNumber(val);
    setPhoneNumberError(val.trim() === "" ? "" : "");
  };

  const onEmail = (val: string) => {
    setEmail(val);
    setEmailError(val.includes("@") ? "" : "");
  };

  const onPassword = (val: string) => {
    setPassword(val);
    setPasswordError(val?.length < 4 ? "" : "");
  };

  const onConfirmPassword = (val: string) => {
    setConfirmPPassword(val);
    setConfirmPPasswordError(val.trim() === "" ? " " : "");
  };

  const resetForm = () => {
    setCountry("");
    setCountryError("");

    setFirstName("");
    setFirstNameError("");

    setLastName("");
    setLastNameError("");

    setMiddleName("");

    setPhoneNumber("");
    setPhoneNumberError("");

    setEmail("");
    setEmailError("");

    setPassword("");
    setPasswordError("");

    setConfirmPPassword("");
    setConfirmPPasswordError("");
  };

  const isFirstNameValid = firstName.trim() !== "";
  const isLastNameValid = lastName.trim() !== "";
  const isPhoneNumberValid = phoneNumber.trim() !== "";
  const isEmailValid = email.trim() !== "" && email.includes("@");
  const isPasswordValid = password.trim() !== "" && password.length >= 4;
  const isConfirmPasswordValid =
    confirmPassword.trim() !== "" && confirmPassword === password;

  const isFormValid =
    isFirstNameValid &&
    isLastNameValid &&
    isPhoneNumberValid &&
    isEmailValid &&
    isPasswordValid &&
    isConfirmPasswordValid &&
    !firstNameError &&
    !lastNameError &&
    !phoneNumberError &&
    !emailError &&
    !passwordError &&
    !confirmPasswordError;

  const createAccountAction = async (e: React.FormEvent) => {
    e.preventDefault();

    const registerInput = new RegisterInput();
    registerInput.country = country.trim();
    registerInput.firstName = firstName.trim();
    registerInput.middleName = middleName.trim();
    registerInput.lastName = lastName.trim();
    registerInput.phoneNumber = phoneNumber.trim();
    registerInput.email = email.trim();
    registerInput.password = password.trim();
    try {
      setIsLoading(true);
      await AuthService.register(registerInput);
      const loginInput = new LoginInput(email.trim(), password.trim());
      resetForm();
      navigate("/auth/login", { state: { loginInput: loginInput } });
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };
  const countryOptions = t('country_options', { returnObjects: true }) as { label: string, value: string }[];
  return (
    <>
      <div className="relative">
        {isLoading && <OverlayLoader message={message} />}

        <div className="w-full h-100">
          <h1 className="text-xl md:text-2xl font-bold leading-tight mt-12">
            {t('signup')}
          </h1>
          <form className="mt-6 mb-3" onSubmit={createAccountAction}>
            <div className="grid gap-6 mb-6 md:grid-cols-2">
              <GenericSelectInput
                label={t('country_label')}
                options={countryOptions}
                value={country}
                onChange={onCountry}
                required
                placeholder={t('select_country')}
                error={countryError}
              />
              <GenericInput
                containerClassName=""
                label={t('first_name_label')}
                value={firstName}
                onChange={onFirstName}
                type="text"
                error={firstNameError}
                placeholder="Enter your first name"
                required={true}
              />
            </div>

            <div className="grid gap-6 mb-6 md:grid-cols-2">
              <GenericInput
                containerClassName=""
                label={t('middle_name_label')}
                value={middleName}
                onChange={onMiddleName}
                type="text"
                placeholder="Enter your middle name"
                required={false}
              />
              <GenericInput
                containerClassName=""
                label={t('last_name_label')}
                value={lastName}
                onChange={onLastName}
                type="text"
                error={lastNameError}
                placeholder="Enter your last name"
                required={true}
              />
            </div>

            <GenericInput
              label={t('phone_number_label')}
              value={phoneNumber}
              onChange={onPhoneNumber}
              type="text"
              error={phoneNumberError}
              placeholder="Enter your phone number"
              required={true}
            />

            <GenericInput
              label={t('email_label')}
              value={email}
              onChange={onEmail}
              type="email"
              error={emailError}
              placeholder="Enter your email"
              required={true}
            />

            <div className="grid gap-6 mb-6 md:grid-cols-2">
              <GenericInput
                containerClassName=""
                label={t('password_label')}
                value={password}
                onChange={onPassword}
                type="password"
                error={passwordError}
                placeholder="Enter your password"
                required={true}
              />

              <GenericInput
                containerClassName=""
                label={t('confirm_password_label')}
                value={confirmPassword}
                onChange={onConfirmPassword}
                type="password"
                error={confirmPasswordError}
                placeholder="Confirm your password"
                required={true}
              />
            </div>

            <div className="mb-6">
              <GenericCheckbox
                id="terms"
                label={t('terms')}
                checked={accepted}
                required
                onChange={(e) => setAccepted(e.target.checked)}
                linkLabel={t('terms_link_2')}
                linkUrl="/terms-and-conditions"
              />
            </div>

            <GenericButton type="submit" disabled={!isFormValid} isFull={false}>
              {t('signup')}
            </GenericButton>
          </form>

          <div className="my-6 border-gray-300 w-full"></div>

          <p className="mt-8 text-center">
            {t('signin_prompt_3')} <GenericLink to="/auth/login" label={t('signin_prompt_4')} />
          </p>
        </div>
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
